import {
  APIListQueryOptions,
  APIListQueryResult,
  findAPIListQueryItem,
  useAPIListQuery,
} from 'shared/api/APIListQuery';
import {
  BundledShipementLoadsDTO,
  ShipmentBuildPreviewDTO,
  ShipmentBuildPreviewParamsDTO,
  ShipmentCarrierDTO,
  ShipmentCarrierParamsDTO,
  ShipmentCityDTO,
  ShipmentCityParamsDTO,
  ShipmentCustomerDTO,
  ShipmentDTO,
  ShipmentSubhaulPreviewDTO,
  SubhaulDTO,
  SubhaulLoadDTO,
  SubhaulResponseDTO,
  SubhauledLoadCountDTO,
  SubhauledVehicleDTO,
  VehiclesPageFiltersDTO,
  VehiclesShipmentCountDTO,
  ShipmentsLastUpdatedDTO,
  shipmentSchema,
  shipmentsLastUpdatedSchema,
  CarrierEDIIntegrationDTO,
  CarrierEDIIntegrationUpdateDTO,
  CarrierEDIOptionsDTO,
  CarrierEDIPartnerUpdateInputDTO,
  carrierEDIIntegrationSchema,
  carrierEDIOptionsSchema,
  SubmitCarrierEDIIntegrationDTO,
  VehiclesSetupCompanyInfoDTO,
  VehiclesSetupConnectionDetailsDTO,
} from './VehiclesDTO';
import {
  APIResponse,
  request,
  requestCarrierAPI,
  requestCarrierAPINoContent,
} from 'shared/api/CarrierAPIClient';
import {
  APIMutationOptions,
  APIMutationResult,
  useAPIMutation,
} from 'shared/api/APIMutation';
import {
  APIQueryOptions,
  APIQueryResult,
  useAPIQuery,
} from 'shared/api/APIQuery';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FIVE_MINUTES, FIVE_SECONDS } from 'shared/constants/NumberConstants';
import { LoadDTO } from 'carrier/loads/data/LoadDTO';

export function mapFiltersToParams(filters?: VehiclesPageFiltersDTO) {
  return {
    vehicle:
      !!filters?.vehicle_name && filters.vehicle_name.length > 2
        ? filters.vehicle_name
        : undefined,
    status:
      filters?.status === 'all'
        ? undefined
        : (filters?.status as VehiclesPageFiltersDTO['status']),
    customer: filters?.customer,
    origin_city: filters?.origin_city,
    origin_state: filters?.origin_state,
    origin_name: filters?.origin_name,
    origin_address: filters?.origin_address,
    origin_zip: filters?.origin_zip,
    destination_city: filters?.destination_city,
    destination_state: filters?.destination_state,
    destination_name: filters?.destination_name,
    destination_address: filters?.destination_address,
    destination_zip: filters?.destination_zip,
    ordering: filters?.ordering,
  };
}

function mapSubhauledFiltersToParams(
  params?: VehiclesPageFiltersDTO,
): Partial<VehiclesPageFiltersDTO> {
  return {
    query:
      !!params?.query && params.query.length > 2 ? params.query : undefined,
    status:
      params?.status === 'all'
        ? undefined
        : (params?.status as VehiclesPageFiltersDTO['status']),
    ordering: params?.ordering,
  };
}

export const SHIPMENT_LIST_QUERY_KEY: [string, string] = ['shipment', 'list'];
export function useShipmentList(
  params?: VehiclesPageFiltersDTO,
  options?: APIListQueryOptions<ShipmentDTO>,
): APIListQueryResult<ShipmentDTO> {
  const query = mapFiltersToParams(params);

  return useAPIListQuery(
    [...SHIPMENT_LIST_QUERY_KEY, { query }],
    (page) =>
      requestCarrierAPI([
        `GET /internal/web/shipments/{?page,params*}`,
        { page, params: { page_size: 50, ...mapFiltersToParams(params) } },
      ]),
    { ...options, keepPreviousData: false, gcTime: 0 },
  );
}

export function useDeleteShipments(options?: APIMutationOptions<string[]>) {
  const queryClient = useQueryClient();

  return useAPIMutation(
    (guids: string[]) =>
      requestCarrierAPI([
        'DELETE /internal/web/shipments/?guids={guids}',
        { guids: guids.join(',') },
      ]),
    {
      ...options,
      onSuccess: (...args) => {
        void queryClient.invalidateQueries({
          queryKey: SHIPMENT_LIST_QUERY_KEY,
        });
        return options?.onSuccess?.(...args);
      },
    },
  );
}

export function useShipmentDetails({
  guid,
  options,
}: {
  guid: string;
  options?: APIQueryOptions<ShipmentDTO>;
}) {
  const queryClient = useQueryClient();
  const initialData = useMemo(
    () => findShipment(queryClient, guid),
    [guid, queryClient],
  );

  return useAPIQuery<ShipmentDTO>(
    ['shipments', 'details', { guid }],
    () => requestCarrierAPI(['GET /internal/web/shipments/{guid}/', { guid }]),
    { enabled: !!guid, schema: shipmentSchema, initialData, ...options },
  );
}

export function useBundledShipementLoads(
  query: string,
  options?: APIQueryOptions<BundledShipementLoadsDTO[]>,
) {
  return useAPIQuery<BundledShipementLoadsDTO[]>(
    ['shipments', 'bundled-loads', { query }],
    () =>
      requestCarrierAPI([
        'GET /internal/web/shipments/loads/?vehicle={query}',
        { query },
      ]),
    { enabled: !!query, ...options },
  );
}

function findShipment(queryClient: QueryClient, guid: string) {
  return findAPIListQueryItem(
    SHIPMENT_LIST_QUERY_KEY,
    queryClient,
    (shipment: ShipmentDTO) => shipment.guid === guid,
  );
}

export function useShipmentCustomerList(
  query?: string,
  options?: APIListQueryOptions<ShipmentCustomerDTO>,
): APIListQueryResult<ShipmentCustomerDTO> {
  return useAPIListQuery(
    ['shipments', 'customer-list', { query }],
    () =>
      requestCarrierAPI([
        `GET /internal/web/shipments/customers/{?params*}`,
        { params: { q: query } },
      ]),
    { ...options },
  );
}

export function useShipmentCityList(
  params?: ShipmentCityParamsDTO,
  options?: APIListQueryOptions<ShipmentCityDTO>,
): APIListQueryResult<ShipmentCityDTO> {
  return useAPIListQuery(
    ['shipments', 'city-list', { params }],
    () =>
      requestCarrierAPI([
        `GET /internal/web/shipments/cities/{?params*}`,
        { params },
      ]),
    { ...options },
  );
}

export function useShipmentBuildPreview(
  params?: ShipmentBuildPreviewParamsDTO,
  options?: APIQueryOptions<ShipmentBuildPreviewDTO[]>,
): APIQueryResult<ShipmentBuildPreviewDTO[]> {
  return useAPIQuery(
    ['shipments', 'build-preview', { params }],
    () =>
      requestCarrierAPI([
        `GET /internal/web/shipments/build/preview/{?params*}`,
        { params },
      ]),
    { ...options, staleTime: FIVE_SECONDS },
  );
}

export function useShipmentSubhaulPreview(
  params?: ShipmentBuildPreviewParamsDTO,
  options?: APIQueryOptions<ShipmentSubhaulPreviewDTO[]>,
): APIQueryResult<ShipmentSubhaulPreviewDTO[]> {
  return useAPIQuery(
    ['shipments', 'subhaul-preview', { params }],
    () =>
      requestCarrierAPI([
        `GET /internal/web/shipments/subhaul/preview/{?params*}`,
        { params },
      ]),
    { ...options, staleTime: FIVE_MINUTES },
  );
}

export function useBuildShipments(
  options?: APIMutationOptions<ShipmentBuildPreviewParamsDTO, string[]>,
): APIMutationResult<ShipmentBuildPreviewParamsDTO, string[]> {
  const invalidateShipmentList = useInvalidateShipmentList();

  return useAPIMutation(
    (params: ShipmentBuildPreviewParamsDTO) =>
      requestCarrierAPI([
        'POST /internal/web/shipments/build/{?params*}',
        { params },
      ]),
    {
      ...options,
      onSuccess: (...args) => {
        invalidateShipmentList();
        return options?.onSuccess?.(...args);
      },
    },
  );
}

export function useInvalidateShipmentList() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({
      queryKey: SHIPMENT_LIST_QUERY_KEY,
    });
  };
}

export function useSubhaulerListCache() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({
      queryKey: SUBHAULED_LIST_QUERY_KEY,
    });
  };
}

export function useMarkAsUnpaid(
  options: APIMutationOptions<string, void> = {},
) {
  return useAPIMutation(
    (guid: string) =>
      requestCarrierAPI([
        'POST /internal/web/loads/{guid}/mark-as-unpaid/',
        { guid },
      ]),
    options,
  );
}

export function useMarkAsPaidToSubhauler(
  options: APIMutationOptions<string, void> = {},
) {
  return useAPIMutation(
    (guid: string) =>
      requestCarrierAPI([
        'POST /internal/web/loads/subhauled/{guid}/subhauler/mark-as-paid/',
        { guid },
      ]),
    options,
  );
}

export function useMarkAsUnpaidToSubhauler(
  options: APIMutationOptions<string, void> = {},
) {
  return useAPIMutation(
    (guid: string) =>
      requestCarrierAPI([
        'POST /internal/web/loads/subhauled/{guid}/subhauler/mark-as-unpaid/',
        { guid },
      ]),
    options,
  );
}

export function getBolPDF(guid: string) {
  return request([
    'GET /internal/web/loads/subhauled/{guid}/bol/pdf/',
    { guid },
  ]);
}

export function useSubhauledLoadDetails(
  options?: APIMutationOptions<string, LoadDTO>,
): APIMutationResult<string, LoadDTO> {
  return useAPIMutation(
    (guid: string) =>
      requestCarrierAPI([
        'GET /internal/web/loads/subhauled/{guid}/',
        { guid },
      ]),
    options,
  );
}

export function useCarriers(
  params?: ShipmentCarrierParamsDTO,
  options?: APIQueryOptions<ShipmentCarrierDTO[]>,
): APIQueryResult<ShipmentCarrierDTO[]> {
  return useAPIQuery(
    ['shipments', 'subhaul-carriers', { params }],
    () =>
      requestCarrierAPI(['GET /internal/web/carriers/{?params*}', { params }]),
    options,
  );
}

export const SHIPMENT_COUNT_QUERY_KEY = ['shipments', 'count'];

export function useVehiclesShipmentCount(
  options?: APIQueryOptions<VehiclesShipmentCountDTO>,
): APIQueryResult<VehiclesShipmentCountDTO> {
  return useAPIQuery(
    SHIPMENT_COUNT_QUERY_KEY,
    () => requestCarrierAPI('GET /internal/web/shipments/count/'),
    options,
  );
}

export const SHIPMENTS_LAST_UPDATED_QUERY_KEY = ['shipments', 'last-updated'];

export function useShipmentsLastUpdated(
  options?: APIQueryOptions<ShipmentsLastUpdatedDTO>,
): APIQueryResult<ShipmentsLastUpdatedDTO> {
  return useAPIQuery(
    SHIPMENTS_LAST_UPDATED_QUERY_KEY,
    () => requestCarrierAPI('GET /internal/web/shipments/last-updated/'),
    { ...options, schema: shipmentsLastUpdatedSchema },
  );
}

export const SUBHAULED_LOAD_COUNT_QUERY_KEY = [
  'shipments',
  'subhauled-load-count',
];

export function useSubhauledLoadCount(
  options?: APIQueryOptions<SubhauledLoadCountDTO>,
): APIQueryResult<SubhauledLoadCountDTO> {
  return useAPIQuery(
    SUBHAULED_LOAD_COUNT_QUERY_KEY,
    () => requestCarrierAPI('GET /internal/web/loads/subhauled/count/'),
    options,
  );
}

export const SUBHAULED_LIST_QUERY_KEY = ['loads', 'subhauled'] as const;

export function useSubhaulAPI() {
  return useMemo(() => {
    function submitSubhaul(json: SubhaulDTO) {
      return requestCarrierAPI<APIResponse<SubhaulResponseDTO[]>>(
        'POST /internal/web/shipments/subhaul/',
        {
          json,
        },
      );
    }

    function submitSubhaulLoad(json: SubhaulLoadDTO, loadGUID: string) {
      return requestCarrierAPI<APIResponse<SubhaulResponseDTO>>(
        `POST /internal/web/loads/${loadGUID}/subhaul/`,
        {
          json,
        },
      );
    }

    function markAsPaidToSubhauler(loadGUID: string) {
      return requestCarrierAPI<APIResponse<SubhaulResponseDTO>>(
        `POST /internal/web/loads/subhauled/${loadGUID}/subhauler/mark-as-paid/`,
      );
    }

    function markAsUnpaidToSubhauler(loadGUID: string) {
      return requestCarrierAPI<APIResponse<SubhaulResponseDTO>>(
        `POST /internal/web/loads/subhauled/${loadGUID}/subhauler/mark-as-unpaid/`,
      );
    }

    return {
      submitSubhaul,
      submitSubhaulLoad,
      markAsPaidToSubhauler,
      markAsUnpaidToSubhauler,
    };
  }, []);
}

export function useSubhauledShipmentList(
  params?: VehiclesPageFiltersDTO,
  options?: APIListQueryOptions<SubhauledVehicleDTO>,
): APIListQueryResult<SubhauledVehicleDTO> {
  const query = mapSubhauledFiltersToParams(params);

  return useAPIListQuery(
    [...SUBHAULED_LIST_QUERY_KEY, { query }],
    (page) =>
      requestCarrierAPI([
        `GET /internal/web/loads/subhauled/{?page,params*}`,
        {
          page,
          params: { page_size: 50, ...mapSubhauledFiltersToParams(params) },
        },
      ]),
    { ...options, keepPreviousData: false, gcTime: 0 },
  );
}

export const CARRIER_EDI_INTEGRATION_QUERY_KEY = [
  'carriers',
  'edi-integration',
];

export function useCarrierEDIIntegration(
  options?: APIQueryOptions<CarrierEDIIntegrationDTO>,
): APIQueryResult<CarrierEDIIntegrationDTO> {
  return useAPIQuery<CarrierEDIIntegrationDTO>(
    CARRIER_EDI_INTEGRATION_QUERY_KEY,
    () => requestCarrierAPI('GET /internal/web/carriers/edi-integration/'),
    {
      ...options,
      refetchOnWindowFocus: false,
      schema: carrierEDIIntegrationSchema,
    },
  );
}

export const CARRIER_EDI_INTEGRATION_OPTIONS_QUERY_KEY = [
  'carriers',
  'edi-integration-options',
];

export function useCarrierEDIIntegrationOptions(
  options?: APIQueryOptions<CarrierEDIOptionsDTO>,
): APIQueryResult<CarrierEDIOptionsDTO> {
  return useAPIQuery<CarrierEDIOptionsDTO>(
    CARRIER_EDI_INTEGRATION_OPTIONS_QUERY_KEY,
    () =>
      requestCarrierAPI('GET /internal/web/carriers/edi-integration/options'),
    {
      ...options,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      schema: carrierEDIOptionsSchema,
    },
  );
}

export function useUpdateCarrierEDIPartner(
  options?: APIMutationOptions<CarrierEDIPartnerUpdateInputDTO, void>,
): APIMutationResult<CarrierEDIPartnerUpdateInputDTO, void> {
  const queryClient = useQueryClient();

  return useAPIMutation(
    ({ guid, ...json }: CarrierEDIPartnerUpdateInputDTO) =>
      requestCarrierAPINoContent(
        [
          'PATCH /internal/web/settings/edi-integration/partners/{guid}',
          { guid },
        ],
        { json },
      ),
    {
      ...options,
      onSuccess: (...args) => {
        void queryClient.invalidateQueries({
          queryKey: CARRIER_EDI_INTEGRATION_QUERY_KEY,
        });
        return options?.onSuccess?.(...args);
      },
    },
  );
}

export function useUpdateCarrierEDIIntegration(
  options?: APIMutationOptions<CarrierEDIIntegrationUpdateDTO, void>,
): APIMutationResult<CarrierEDIIntegrationUpdateDTO, void> {
  const queryClient = useQueryClient();

  return useAPIMutation(
    (json: CarrierEDIIntegrationUpdateDTO) =>
      requestCarrierAPINoContent(
        'PATCH /internal/web/carriers/edi-integration/',
        {
          json,
        },
      ),
    {
      ...options,
      onSuccess: (...args) => {
        void queryClient.invalidateQueries({
          queryKey: CARRIER_EDI_INTEGRATION_QUERY_KEY,
        });
        return options?.onSuccess?.(...args);
      },
    },
  );
}

export function mapVehiclesSetupToSubmitPayload(
  companyInfo: VehiclesSetupCompanyInfoDTO,
  connectionDetails: VehiclesSetupConnectionDetailsDTO,
): SubmitCarrierEDIIntegrationDTO {
  return {
    scac: companyInfo.scac,
    contact: companyInfo.contact,
    partners: (connectionDetails.partners ?? []).map((partner) => ({
      oem: partner.oem,
      relationship_status: partner.relationship_status,
      display_name: partner.display_name,
      load_number_prefix: partner.load_number_prefix,
      expected_vins_per_month: partner.expected_vins_per_month,
      oem_contact:
        partner.oem_contact.name || partner.oem_contact.email
          ? partner.oem_contact
          : undefined,
      epod_providers:
        partner.fenkell_username && partner.fenkell_password
          ? [
              {
                name: 'fenkell',
                username: partner.fenkell_username,
                password: partner.fenkell_password,
              },
            ]
          : [],
    })),
  };
}

export function useSubmitCarrierEDIIntegration(
  options?: APIMutationOptions<
    SubmitCarrierEDIIntegrationDTO,
    CarrierEDIIntegrationDTO
  >,
): APIMutationResult<SubmitCarrierEDIIntegrationDTO, CarrierEDIIntegrationDTO> {
  const queryClient = useQueryClient();

  return useAPIMutation(
    (json: SubmitCarrierEDIIntegrationDTO) =>
      requestCarrierAPI('POST /internal/web/carriers/edi-integration/', {
        json,
      }),
    {
      ...options,
      onSuccess: (...args) => {
        void queryClient.invalidateQueries({
          queryKey: CARRIER_EDI_INTEGRATION_QUERY_KEY,
        });
        return options?.onSuccess?.(...args);
      },
    },
  );
}
